
import { Component, Vue, Prop } from 'vue-property-decorator';
import { CustomReportList } from '@/collections/custom_reports';
import { CustomReportModel } from '@/models/custom_report';
import { MeasureList } from '@/collections/measures';
import { MeasureModel } from '@/models/measure';
import HeaderTitle from '@/components/HeaderTitle.vue';
import ContentLoadResultMessage from '@/components/ContentLoadResultMessage.vue';
import { StoreModel } from '@/models/store';
import { RuleModel } from '@/models/rule';
import Permission from '@/components/Permission.vue';
import LoadingSlider from '@/components/LoadingSlider.vue';
import { DashboardList } from '@/collections/dashboards';
import { DashboardModel } from '@/models/dashboard';
import { EventHandler } from '@/modules/events';
import EmptyStage from '@/components/EmptyStage.vue';
import ActionButton from '@/components/ActionButton.vue';
import { FEATURES } from '@/config/featureConfig';


interface GroupReport {
  measure: MeasureModel;
  reports: CustomReportList;
}

@Component({
  components: {
    HeaderTitle,
    ContentLoadResultMessage,
    Permission,
    LoadingSlider,
    EmptyStage,
    ActionButton,
  },
})
export default class CustomDashboards extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  public measures: MeasureList = new MeasureList();
  public loading: boolean = false;
  @Prop({ default: false })
  public enableAthena!: boolean;
  @Prop({ default: false })
  public isDataReady!: boolean;
  @Prop({ default: false })
  public isAthenaReady!: boolean;
  @Prop({ default: false })
  public isAthenaFinished!: boolean;
  @Prop({ default: () => [] })
  public timeRangeLimit!: string[];
  @Prop({ default: false })
  public hasSchemas!: boolean;
  public rule: RuleModel = new RuleModel({ code: 'favorite_reports_count' });
  public addDefaultReportLoading: boolean = false;
  public loadingSlides: string[] = [
    'checking your profile',
    'fetching reports',
    'adding reports',
  ];
  public dashboards: DashboardList = new DashboardList();
  public dialogAddDashboard: boolean = false;
  public newDashboardName: string = '';
  public newDashboardError: boolean = false;
  public lockDashboardRule: RuleModel = new RuleModel({code: FEATURES.ADD_REPORT_DASHBOARD});
  public FEATURES = FEATURES;
  
  public async created() {
    this.loading = true;
    for (const rule of this.store.currentPackage.rules.items) {
      if (rule.code === this.rule.code) {
        this.rule = rule;
        break;
      }
    }
    // await this.measures.fetch({ isOlap: true });
    await this.loadDashboard();
    this.getLockDashboardRule();
    this.loading = false;
  }

  public canAddCustomDashboard() {
    // Try to use permission service first if available
    try {
      if (this.$permissionService) {
        return this.$permissionService.hasPermission(FEATURES.ADD_REPORT_DASHBOARD);
      }
    } catch (e) {
      console.log("Error checking permission with service:", e);
    }
    
    // Fall back to store check
    const validateValues: any = [
      {
        code: FEATURES.ADD_REPORT_DASHBOARD,
      },
    ];
    const validateResult = this.store.validateRule(validateValues);
    
    // Special case: Also check if user has a paid package
    const hasPaidPackage = this.store.currentPackage && 
                           this.store.currentPackage.name && 
                           ['Professional', 'Enterprise'].includes(this.store.currentPackage.name);
    
    return validateResult.isValidate || hasPaidPackage;
  }

  public getLockDashboardRule() {
    for (const rule of this.store.currentPackage.rules.items) {
      if (rule.code === this.lockDashboardRule.code) {
        this.lockDashboardRule = rule;
        break;
      }
    }
  }

  public getDashboardLink(dashboard: DashboardModel) {
    return `/myboard/${dashboard.id}`;
  }

  public async loadDashboard() {
    try {
      this.dashboards = new DashboardList();
      await this.dashboards.fetch();
    } catch (e: any) {
      // do nothing
    }
  }

  public async createDashboard() {
    const newDashboard = new DashboardModel();
    if (this.newDashboardName) {
      newDashboard.name = this.newDashboardName;
      this.dialogAddDashboard = false;
      await newDashboard.create();
      localStorage.setItem('needReloadDashboard', 'true');
      try {
        const eventHandler = new EventHandler({
          store: this.store,
        });
        eventHandler.track('Create Dashboard', {
          dashboardName: newDashboard.name,
          dashboardId: newDashboard.id,
        });
      } catch (e) {
        // ignore this
      }
      this.$router.push('/myboard/' + newDashboard.id);
    } else {
      this.newDashboardError = true;
    }
  }
}
